import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { MapPin, ArrowLeft, RefreshCw, User, Route, AlertTriangle } from 'lucide-react';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const POLLING_INTERVAL_MS = 28000;
// Um expediente com o rastreio em segundo plano funcionando passa de mil pontos.
const PERCURSO_MIN_PONTOS = 100;
const DEFAULT_CENTER = [-15.7801, -47.9292];
const DEFAULT_ZOOM = 4;

function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function fotoSrc(fotoBase64) {
  if (!fotoBase64) return null;
  const raw = String(fotoBase64).trim();
  if (!raw) return null;
  if (raw.startsWith('data:')) return raw;
  return `data:image/jpeg;base64,${raw}`;
}

function hojeSP() {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

function fmtMin(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const m = Math.round(Number(n));
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h ${m % 60}min`;
}

function segmentosDoPercurso(pontos) {
  const pts = (pontos || []).filter((p) => p.latitude != null && p.longitude != null);
  const segs = [];
  let current = [];
  for (let i = 0; i < pts.length; i += 1) {
    const ll = [Number(pts[i].latitude), Number(pts[i].longitude)];
    if (i === 0) {
      current = [ll];
      continue;
    }
    const prev = pts[i - 1];
    const t0 = prev.timestamp ? new Date(prev.timestamp).getTime() : 0;
    const t1 = pts[i].timestamp ? new Date(pts[i].timestamp).getTime() : 0;
    const min = t0 && t1 ? (t1 - t0) / 60000 : 0;
    const km = haversineKm(
      Number(prev.latitude),
      Number(prev.longitude),
      Number(pts[i].latitude),
      Number(pts[i].longitude)
    );
    const isGap = min >= 10 && km >= 2;
    if (isGap) {
      if (current.length >= 2) segs.push({ dashed: false, latlngs: current });
      const last = current[current.length - 1] || ll;
      segs.push({ dashed: true, latlngs: [last, ll], minutos: min, km });
      current = [ll];
    } else {
      current.push(ll);
    }
  }
  if (current.length >= 2) segs.push({ dashed: false, latlngs: current });
  return segs;
}

const MapaTecnicos = () => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const polylinesRef = useRef([]);

  const [modo, setModo] = useState('ao_vivo'); // ao_vivo | percurso
  const [tecnicos, setTecnicos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [percursoTecnicoId, setPercursoTecnicoId] = useState('');
  const [percursoData, setPercursoData] = useState(hojeSP());
  const [percursoPontos, setPercursoPontos] = useState([]);
  const [percursoNome, setPercursoNome] = useState('');
  const [percursoMeta, setPercursoMeta] = useState(null);
  const [loadingPercurso, setLoadingPercurso] = useState(false);
  const [listaTecnicosSelect, setListaTecnicosSelect] = useState([]);

  const fotosCacheRef = useRef({});
  const jaCarregouFotosRef = useRef(false);
  const fitDoneLiveRef = useRef(false);

  const loadAoVivo = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const pedirFoto = !jaCarregouFotosRef.current || !silent;
      const res = await api.get('/tecnicos/ubicacao', {
        params: pedirFoto ? undefined : { incluir_foto: false },
      });
      const lista = (res.data?.tecnicos || []).map((t) => {
        const uid = t.tecnico_id || t.user_id;
        if (t.foto_base64 && uid) {
          fotosCacheRef.current[uid] = t.foto_base64;
        }
        const fotoPreservada = t.foto_base64 || (uid ? fotosCacheRef.current[uid] : null) || null;
        return { ...t, foto_base64: fotoPreservada };
      });
      if (pedirFoto) jaCarregouFotosRef.current = true;
      setTecnicos(lista);
      setListaTecnicosSelect((prev) => {
        const map = new Map(prev.map((t) => [t.id, t]));
        lista.forEach((t) => {
          const id = t.tecnico_id || t.user_id;
          if (id) map.set(id, { id, nome: t.tecnico_nome || id });
        });
        return Array.from(map.values()).sort((a, b) =>
          (a.nome || '').localeCompare(b.nome || '', 'pt-BR')
        );
      });
    } catch (err) {
      if (!silent) {
        toast.error('Erro ao carregar posições');
        setTecnicos([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const loadPercurso = useCallback(async () => {
    if (!percursoTecnicoId || !percursoData) {
      setPercursoPontos([]);
      setPercursoMeta(null);
      return;
    }
    setLoadingPercurso(true);
    try {
      const res = await api.get('/tecnicos/percurso', {
        params: { tecnico_id: percursoTecnicoId, data: percursoData },
      });
      setPercursoPontos(res.data?.pontos || []);
      setPercursoNome(res.data?.tecnico_nome || '');
      setPercursoMeta({
        km: res.data?.km,
        km_estrada: res.data?.km_estrada,
        km_total_rota: res.data?.km_total_rota,
        custo_total: res.data?.custo_total,
        gaps: res.data?.gaps || [],
        tempo_movimento_min: res.data?.tempo_movimento_min,
        tempo_parado_min: res.data?.tempo_parado_min,
        tempo_sem_sinal_min: res.data?.tempo_sem_sinal_min,
        paradas: res.data?.paradas || [],
        rodovias: res.data?.rodovias || [],
      });
      if (!(res.data?.pontos || []).length) {
        toast('Nenhum ponto de percurso neste dia', { icon: '📍' });
      }
    } catch {
      toast.error('Erro ao carregar percurso');
      setPercursoPontos([]);
      setPercursoMeta(null);
    } finally {
      setLoadingPercurso(false);
    }
  }, [percursoTecnicoId, percursoData]);

  useEffect(() => {
    loadAoVivo();
  }, [loadAoVivo]);

  useEffect(() => {
    if (modo !== 'ao_vivo') return undefined;
    const tick = () => {
      if (document.visibilityState === 'visible') {
        loadAoVivo({ silent: true });
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadAoVivo({ silent: true });
      }
    };
    const id = setInterval(tick, POLLING_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [modo, loadAoVivo]);

  useEffect(() => {
    if (modo === 'percurso' && percursoTecnicoId) loadPercurso();
  }, [modo, percursoTecnicoId, percursoData, loadPercurso]);

  // Carregar lista de técnicos (users) se mapa ao vivo vazio
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/users', { params: { funcao: 'tecnico' } });
        const users = res.data?.users || res.data || [];
        if (!Array.isArray(users) || cancelled) return;
        setListaTecnicosSelect((prev) => {
          const map = new Map(prev.map((t) => [t.id, t]));
          users.forEach((u) => {
            if (u?.id) map.set(u.id, { id: u.id, nome: u.name || u.email || u.id });
          });
          return Array.from(map.values()).sort((a, b) =>
            (a.nome || '').localeCompare(b.nome || '', 'pt-BR')
          );
        });
      } catch {
        // lista do ao vivo basta
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const formatTime = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return iso;
    }
  };

  const mapsUrl = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;

  const tecnicosComPosicao = useMemo(
    () =>
      tecnicos.filter(
        (t) => t.latitude != null && t.longitude != null && Number(t.latitude) && Number(t.longitude)
      ),
    [tecnicos]
  );
  const hasValidPositions = tecnicosComPosicao.length > 0;

  const percursoLatLngs = useMemo(
    () =>
      (percursoPontos || [])
        .filter((p) => p.latitude != null && p.longitude != null)
        .map((p) => [Number(p.latitude), Number(p.longitude)]),
    [percursoPontos]
  );
  const percursoSegmentos = useMemo(() => segmentosDoPercurso(percursoPontos), [percursoPontos]);
  const tecnicosGpsParado = useMemo(
    () => (tecnicos || []).filter((t) => t.gps_stale),
    [tecnicos]
  );
  const tecnicosRastreioParado = useMemo(
    () => (tecnicos || []).filter((t) => t.rastreio_bg_parado),
    [tecnicos]
  );
  const percursoPoucosPontos =
    modo === 'percurso' &&
    !loadingPercurso &&
    percursoLatLngs.length > 0 &&
    percursoLatLngs.length < PERCURSO_MIN_PONTOS;

  // Mapa Leaflet
  useEffect(() => {
    const showLive = modo === 'ao_vivo' && hasValidPositions;
    const showTrail = modo === 'percurso' && percursoLatLngs.length > 0;

    if (!showLive && !showTrail) {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markersRef.current = [];
        polylinesRef.current = [];
      }
      fitDoneLiveRef.current = false;
      return undefined;
    }
    if (!mapRef.current) return undefined;

    if (!mapInstanceRef.current) {
      const map = L.map(mapRef.current).setView(DEFAULT_CENTER, DEFAULT_ZOOM);
      mapInstanceRef.current = map;
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      }).addTo(map);
    }

    const map = mapInstanceRef.current;
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];
    polylinesRef.current.forEach((l) => l.remove());
    polylinesRef.current = [];

    if (showLive) {
      const positions = tecnicosComPosicao.map((t) => [Number(t.latitude), Number(t.longitude)]);
      if (!fitDoneLiveRef.current) {
        if (positions.length === 1) map.setView(positions[0], 14);
        else if (positions.length > 1) {
          map.fitBounds(L.latLngBounds(positions), { padding: [40, 40], maxZoom: 14 });
        }
        fitDoneLiveRef.current = true;
      }
      markersRef.current = tecnicosComPosicao.map((t) => {
        const marker = L.marker([Number(t.latitude), Number(t.longitude)]).addTo(map);
        const foto = fotoSrc(t.foto_base64);
        const sobre = (t.sobre || '').trim();
        marker.bindPopup(`
          <div class="min-w-[200px]">
            <div style="display:flex;gap:10px;align-items:flex-start">
              ${
                foto
                  ? `<img src="${foto}" alt="" style="width:48px;height:48px;border-radius:9999px;object-fit:cover;flex-shrink:0" />`
                  : `<div style="width:48px;height:48px;border-radius:9999px;background:#e2e8f0;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:18px">👤</div>`
              }
              <div>
                <p class="font-bold text-slate-900">${escapeHtml(t.tecnico_nome || '')}</p>
                <p class="text-sm text-slate-500 mt-1">${t.em_servico ? 'Em serviço' : 'Rota ativa'}</p>
                ${
                  sobre
                    ? `<p class="text-sm text-slate-600 mt-2" style="white-space:pre-wrap">${escapeHtml(sobre)}</p>`
                    : ''
                }
              </div>
            </div>
            <a href="https://www.google.com/maps?q=${t.latitude},${t.longitude}" target="_blank" rel="noopener noreferrer" class="mt-2 inline-block text-emerald-600 text-sm font-medium hover:underline">Abrir no Google Maps</a>
          </div>
        `);
        return marker;
      });
    }

    if (showTrail) {
      fitDoneLiveRef.current = false;
      const layers = (percursoSegmentos.length ? percursoSegmentos : [{ dashed: false, latlngs: percursoLatLngs }]).map(
        (seg) => {
          const line = L.polyline(seg.latlngs, {
            color: seg.dashed ? '#d97706' : '#059669',
            weight: seg.dashed ? 3 : 4,
            opacity: 0.9,
            dashArray: seg.dashed ? '8, 10' : null,
          }).addTo(map);
          if (seg.dashed) {
            line.bindPopup(
              `Sem sinal · ${Math.round(seg.minutos || 0)} min · ${(seg.km || 0).toFixed(1)} km`
            );
          }
          return line;
        }
      );
      polylinesRef.current = layers;
      map.fitBounds(L.latLngBounds(percursoLatLngs), { padding: [40, 40], maxZoom: 15 });
      const first = percursoLatLngs[0];
      const last = percursoLatLngs[percursoLatLngs.length - 1];
      const marks = [
        L.circleMarker(first, { radius: 8, color: '#2563eb', fillColor: '#3b82f6', fillOpacity: 1 })
          .addTo(map)
          .bindPopup('Início'),
        L.circleMarker(last, { radius: 8, color: '#dc2626', fillColor: '#ef4444', fillOpacity: 1 })
          .addTo(map)
          .bindPopup('Fim / última posição'),
      ];
      (percursoMeta?.paradas || []).forEach((p) => {
        if (p.latitude == null || p.longitude == null) return;
        const dur = p.duracao_minutos != null ? `${Math.round(p.duracao_minutos)} min` : '';
        marks.push(
          L.circleMarker([Number(p.latitude), Number(p.longitude)], {
            radius: 7,
            color: '#7c3aed',
            fillColor: '#a78bfa',
            fillOpacity: 1,
          })
            .addTo(map)
            .bindPopup(
              `${escapeHtml(p.tipo || 'parada')}${dur ? ` · ${dur}` : ''}${
                p.local_nome ? `<br/>${escapeHtml(p.local_nome)}` : ''
              }`
            )
        );
      });
      markersRef.current = marks;
    }

    return undefined;
  }, [modo, hasValidPositions, tecnicosComPosicao, percursoLatLngs, percursoSegmentos, percursoMeta]);

  useEffect(() => () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }
  }, []);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Link to="/rotas" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-5 h-5" /> Voltar
          </Link>
          <div className="w-12 h-12 rounded-xl bg-indigo-500 flex items-center justify-center">
            <MapPin className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Onde estão os técnicos</h1>
            <p className="text-slate-600 text-sm">
              Ao vivo (~30s) ou percurso do dia gravado pelo app
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="bg-slate-100 rounded-xl p-1 flex">
            <button
              type="button"
              onClick={() => setModo('ao_vivo')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                modo === 'ao_vivo' ? 'bg-white shadow text-slate-900' : 'text-slate-600'
              }`}
            >
              Ao vivo
            </button>
            <button
              type="button"
              onClick={() => setModo('percurso')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1 ${
                modo === 'percurso' ? 'bg-white shadow text-slate-900' : 'text-slate-600'
              }`}
            >
              <Route className="w-4 h-4" /> Percurso do dia
            </button>
          </div>
          <button
            onClick={() => (modo === 'ao_vivo' ? loadAoVivo() : loadPercurso())}
            disabled={loading || loadingPercurso}
            className="bg-slate-100 text-slate-700 px-4 py-2 rounded-xl font-medium flex items-center gap-2 hover:bg-slate-200 disabled:opacity-50"
          >
            <RefreshCw
              className={`w-4 h-4 ${loading || loadingPercurso ? 'animate-spin' : ''}`}
            />{' '}
            Atualizar
          </button>
        </div>
      </div>

      {modo === 'percurso' && (
        <div className="bg-white rounded-2xl shadow-sm p-4 border border-slate-100 flex flex-wrap gap-4 items-end">
          <div className="min-w-[200px] flex-1">
            <label className="block text-sm font-medium text-slate-600 mb-1">Técnico</label>
            <select
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm"
              value={percursoTecnicoId}
              onChange={(e) => setPercursoTecnicoId(e.target.value)}
            >
              <option value="">Selecione…</option>
              {listaTecnicosSelect.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Data</label>
            <input
              type="date"
              className="border border-slate-200 rounded-xl px-3 py-2 text-sm"
              value={percursoData}
              onChange={(e) => setPercursoData(e.target.value)}
            />
          </div>
          <button
            type="button"
            onClick={loadPercurso}
            disabled={!percursoTecnicoId || loadingPercurso}
            className="bg-emerald-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
          >
            Ver percurso
          </button>
          {percursoLatLngs.length > 0 && (
            <p className="text-sm text-slate-500 self-center">
              {percursoNome ? `${percursoNome} · ` : ''}
              {percursoLatLngs.length} ponto(s)
            </p>
          )}
        </div>
      )}

      {modo === 'ao_vivo' && tecnicosGpsParado.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <p className="text-sm font-semibold text-red-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            GPS parado há mais de 15 min
          </p>
          <ul className="mt-2 text-sm text-red-700 space-y-1">
            {tecnicosGpsParado.map((t) => (
              <li key={t.tecnico_id}>
                {t.tecnico_nome || 'Técnico'}
                {t.segundos_sem_atualizacao != null
                  ? ` · último ponto há ${Math.round(t.segundos_sem_atualizacao / 60)} min`
                  : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {modo === 'ao_vivo' && tecnicosRastreioParado.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <p className="text-sm font-semibold text-red-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Rastreio em segundo plano parado
          </p>
          <p className="text-xs text-red-700 mt-1">
            O app não está enviando a posição com a tela apagada ou o app fechado. O percurso do dia
            fica com buracos. Confira se o técnico está com a versão mais recente do app.
          </p>
          <ul className="mt-2 text-sm text-red-700 space-y-1">
            {tecnicosRastreioParado.map((t) => (
              <li key={t.tecnico_id}>
                {t.tecnico_nome || 'Técnico'}
                {t.rastreio_bg_ultimo
                  ? ` · parado desde ${formatTime(t.rastreio_bg_ultimo)}`
                  : ' · nenhum ponto em segundo plano hoje'}
                {` · ${t.pontos_hoje || 0} ponto(s) no dia`}
              </li>
            ))}
          </ul>
        </div>
      )}

      {percursoPoucosPontos && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Poucos pontos neste dia ({percursoLatLngs.length})
          </p>
          <p className="text-xs text-amber-700 mt-1">
            O rastro está incompleto: o rastreio em segundo plano do app provavelmente não estava
            enviando. A linha no mapa liga só os pontos que chegaram.
          </p>
        </div>
      )}

      {modo === 'percurso' && percursoLatLngs.length > 0 && percursoMeta && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500">KM de estrada</p>
            <p className="text-xl font-bold text-slate-900">
              {percursoMeta.km_estrada != null && Number(percursoMeta.km_estrada) > 0
                ? `${Number(percursoMeta.km_estrada).toFixed(1)} km`
                : percursoMeta.km_total_rota != null && Number(percursoMeta.km_total_rota) > 0
                  ? `${Number(percursoMeta.km_total_rota).toFixed(1)} km`
                  : percursoMeta.km != null
                    ? `${Number(percursoMeta.km).toFixed(1)} km`
                    : '—'}
            </p>
            {percursoMeta.km != null && (
              <p className="text-xs text-slate-400 mt-1">GPS bruto: {Number(percursoMeta.km).toFixed(1)} km</p>
            )}
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500">Em movimento</p>
            <p className="text-xl font-bold text-slate-900">{fmtMin(percursoMeta.tempo_movimento_min)}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500">Parado</p>
            <p className="text-xl font-bold text-slate-900">{fmtMin(percursoMeta.tempo_parado_min)}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500">Sem sinal</p>
            <p className="text-xl font-bold text-amber-700">{fmtMin(percursoMeta.tempo_sem_sinal_min)}</p>
            {(percursoMeta.gaps || []).length > 0 && (
              <p className="text-xs text-slate-400 mt-1">
                {(percursoMeta.gaps || []).length} buraco(s) no rastro
              </p>
            )}
          </div>
        </div>
      )}

      {modo === 'ao_vivo' && loading && tecnicos.length === 0 ? (
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : modo === 'ao_vivo' && !hasValidPositions ? (
        <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
          <MapPin className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Nenhum técnico com localização recente</p>
          <p className="text-slate-400 text-sm mt-2">
            O app envia a posição quando o técnico está em serviço
          </p>
        </div>
      ) : modo === 'percurso' && !percursoTecnicoId ? (
        <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
          <Route className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Selecione um técnico e a data do percurso</p>
        </div>
      ) : modo === 'percurso' && percursoTecnicoId && !loadingPercurso && percursoLatLngs.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
          <Route className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Sem percurso gravado neste dia</p>
        </div>
      ) : (
        <>
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white">
            <div ref={mapRef} className="h-[420px] w-full" />
          </div>

          {modo === 'ao_vivo' && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {tecnicos.map((t) => {
                const foto = fotoSrc(t.foto_base64);
                const sobre = (t.sobre || '').trim();
                return (
                  <div
                    key={t.tecnico_id}
                    className="bg-white rounded-2xl shadow-sm p-5 border border-slate-100"
                  >
                    <div className="flex items-start gap-3">
                      {foto ? (
                        <img
                          src={foto}
                          alt={t.tecnico_nome || 'Técnico'}
                          className="w-14 h-14 rounded-full object-cover flex-shrink-0 border border-slate-200"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 text-slate-400">
                          <User className="w-7 h-7" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-slate-900 truncate">{t.tecnico_nome}</p>
                        <p className="text-sm text-slate-500 mt-0.5">
                          {t.em_servico ? 'Em serviço' : 'Rota ativa'} · atualizado{' '}
                          {formatTime(t.updated_at)}
                          {t.gps_stale ? ' · GPS parado' : ''}
                        </p>
                        {(t.app_version || t.rastreio_nativo === false) && (
                          <p
                            className={`text-xs mt-1 ${
                              t.rastreio_nativo === false ? 'text-red-600 font-medium' : 'text-slate-400'
                            }`}
                          >
                            {t.app_version ? `App v${t.app_version}` : 'App'}
                            {t.rastreio_nativo === false
                              ? ' · build SEM rastreio em segundo plano (atualizar o app)'
                              : ''}
                          </p>
                        )}
                        {t.rastreio_bg_parado && (
                          <p className="text-xs font-medium text-red-600 mt-1">
                            Rastreio em segundo plano parado
                            {t.rastreio_bg_ultimo ? ` desde ${formatTime(t.rastreio_bg_ultimo)}` : ''}
                          </p>
                        )}
                        {sobre ? (
                          <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap break-words">
                            {sobre}
                          </p>
                        ) : (
                          <p className="text-xs text-slate-400 mt-2 italic">Sem texto “sobre”</p>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-3">
                      {t.latitude != null && t.longitude != null
                        ? `${Number(t.latitude).toFixed(5)}, ${Number(t.longitude).toFixed(5)}`
                        : '—'}
                    </p>
                    {t.latitude != null && t.longitude != null && (
                      <a
                        href={mapsUrl(t.latitude, t.longitude)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-flex items-center gap-2 text-emerald-600 font-medium hover:underline"
                      >
                        <MapPin className="w-4 h-4" /> Ver no Google Maps
                      </a>
                    )}
                    <button
                      type="button"
                      className="mt-3 block text-sm text-indigo-600 font-medium hover:underline"
                      onClick={() => {
                        setPercursoTecnicoId(t.tecnico_id);
                        setPercursoData(hojeSP());
                        setModo('percurso');
                      }}
                    >
                      Ver percurso de hoje
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {modo === 'percurso' && (percursoMeta?.paradas || []).length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm p-5 border border-slate-100">
              <h2 className="font-bold text-slate-900 mb-3">Paradas</h2>
              <ul className="space-y-2">
                {(percursoMeta.paradas || []).map((p, i) => (
                  <li key={p.id || i} className="text-sm text-slate-700 flex justify-between gap-3">
                    <span>
                      {p.tipo || 'parada'}
                      {p.local_nome ? ` · ${p.local_nome}` : ''}
                      {p.cliente_nome ? ` · ${p.cliente_nome}` : ''}
                    </span>
                    <span className="text-slate-500 whitespace-nowrap">
                      {formatTime(p.hora_chegada)}
                      {p.duracao_minutos != null ? ` · ${Math.round(p.duracao_minutos)} min` : ''}
                    </span>
                  </li>
                ))}
              </ul>
              {(percursoMeta.rodovias || []).length > 0 && (
                <p className="text-xs text-slate-400 mt-3">
                  Vias: {(percursoMeta.rodovias || []).join(', ')}
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MapaTecnicos;
